/** @format */

import React, { useState, useMemo, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { _$, Icon, Table, TableColumn, Tooltip, Checkbox, Dialog, Message, Spin } from 'kpc-react'
import { useParams, useHistory } from 'react-router-dom'
import MonitorDashboardChart, { MonitorChartItemAppHandle } from './MonitorDashboardChar'
import { IMonitorViewProps, chartTableScheme } from './constants'
import getMetricStatisticsBatch from './getMetricStatisticsBatch'
import { cloneDeep } from 'lodash'
import { useWindowSize, useDebounce, useDeepCompareEffect } from 'react-use'
import { request, handleExportForm, keepDecimal, handleUnit } from 'src/utils'
import MonitorChartDelete from './MonitorChartDelete'
import MonitorChartExport from './MonitorChartExport'
import MonitorChartDetails from './MonitorChartDetails'
import variable from 'src/static/styles/var'
// eslint-disable-next-line max-len
import { aggregator_menu } from 'src/containers/MonitorDashboard/Customboard/EditGraphMetricFormItem'

const initTableScheme = chartTableScheme.map(({ key }) => key)

const MonitorView: React.FC<IMonitorViewProps> = ({
  className,
  productList,
  dashboardName,
  graph,
  start,
  end,
  delay,
  reload
}) => {
  const { id: dashboardID } = useParams<{ name: string; id: string }>()
  const history = useHistory()
  const [tableVisible, setTableVisible] = useState<boolean>(false) // 打开/关闭table
  const [tableScheme, setTableScheme] = useState<string[]>(initTableScheme) // 当前schemem
  // 临时scheme - tooltip确定/取消之前
  const [schemeChecked, setSchemeChecked] = useState<string[]>(initTableScheme)
  // table sort
  const [tableSort, setTableSort] = useState({})
  const [tableData, setTableData] = useState<any[]>([])
  const [allAggregator, setAllAggregator] = useState<boolean>(false)
  const [deleteVisible, setDeleteVisible] = useState<boolean>(false) // 打开/关闭删除
  // const [exportVisible, setExportVisible] = useState<boolean>(false) // 打开/关闭导出
  const [detailsVisible, setDetailsVisible] = useState<boolean>(false) // 打开/关闭图表详情
  // chartItem Ref
  const chartItemRef = useRef<MonitorChartItemAppHandle>()

  // 监控图表是否再可视区域内
  const observer = new IntersectionObserver(entries => {
    if (entries[0].intersectionRatio > 0) {
      const container = entries[0].target
      getDashboardChart(start, end, delay, graph, undefined, false, true)
      observer.unobserve(container)
    }
  })

  // 导出监控数据
  const exportChartData = async () => {
    const chartData = await getDashboardChart(
      start,
      end,
      delay,
      graph,
      delay < 60 ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD HH:mm'
    )
    const headers = [{ key: 'time', value: _$('时间') }].concat(
      chartData?.series.map(({ name }) => {
        const [metricName, unit] = name.split('===')
        return {
          key: unit ? `${metricName}(${unit})` : metricName,
          value: unit ? `${metricName}(${unit})` : metricName
        }
      })
    )
    const dataSource = []
    chartData?.xData.forEach((date, i) => {
      const dataMap = chartData.series.map(({ data }) =>
        typeof data[i] === 'number' ? data[i] : ''
      )
      dataSource.push([date, ...dataMap])
    })
    if (dataSource?.length) {
      handleExportForm(headers, dataSource, graph.title)
      Message.success(_$('下载成功'))
    } else {
      Message.error(_$('所选监控项暂无数据'))
    }
  }

  // 浏览器尺寸变换后关闭所有table
  const { width: winWidth, height: winHeight } = useWindowSize()
  useDebounce(
    () => {
      setTableVisible(false)
    },
    300,
    [winWidth, winHeight]
  )

  // table scheme
  const scheme = useMemo(() => {
    if (tableVisible) {
      const checkedScheme = chartTableScheme.filter(({ key }) => tableScheme.includes(key))
      const scheme = {}
      for (const i in checkedScheme) {
        const { title, key } = checkedScheme[i]
        scheme[key] = {
          title: _$(title),
          key
        }
        if (scheme[key]?.key !== 'name') {
          scheme[key].width = 100
          scheme[key].sortable = true
          scheme[key].template = record => {
            if (record[key] === '-') {
              return '-'
            }
            const units = record?.units || ''
            return `${handleUnit(record[key], units)}`
          }
        } else {
          scheme[key].fixed = 'left'
        }
      }
      return scheme
    } else {
      return {}
    }
  }, [tableVisible, tableScheme])

  // dashboard query 接口
  const { chartData, getDashboardChart, loading } = getMetricStatisticsBatch()

  // table data
  useDeepCompareEffect(() => {
    if (chartData?.series?.length) {
      const result = []
      if (chartData?.series.every(item => item.resourceAggregator)) {
        setAllAggregator(true)
        setTableData([])
      } else {
        chartData?.series.forEach(({ name, data, resourceAggregator }) => {
          if (resourceAggregator) {
            return
          }
          const filterData = data.filter(count => count !== null && count !== undefined)
          let total = 0
          filterData.forEach(count => {
            total += count
          })
          const nameSplit = name.split('===')
          const max = filterData?.length ? Math.max.apply(null, filterData) : '-'
          const min = filterData?.length ? Math.min.apply(null, filterData) : '-'
          const avg = filterData?.length ? keepDecimal(total / filterData?.length) : '-'
          const sum = filterData?.length ? keepDecimal(total) : '-'
          result.push({
            name: nameSplit[0],
            units: nameSplit[1],
            max,
            min,
            avg,
            sum
          })
        })
        setTableData(result)
      }
    } else {
      setTableData([])
    }
  }, [chartData?.series])

  // 开始观察/卸载ovserver
  useEffect(() => {
    observer.observe(document.getElementById(`dashboard-chart-${graph.graphId}`))
    return () => {
      observer.disconnect()
    }
  }, [start, end, delay])

  // 下载table
  const tableDataeExport = () => {
    const header = Object.values(scheme).map((item: any) => {
      return {
        key: item.key,
        value: item.title
      }
    })
    const dataSource = tableData.map(({ name, max, min, avg, sum }) =>
      [name, max, min, avg, sum].map(item => (item + '').replaceAll(',', ';'))
    )
    handleExportForm(header, dataSource, graph.title)
    Message.success(_$('下载成功'))
  }

  const tableH = useMemo(() => {
    if (tableData?.length) {
      return tableData.length > 10 ? 10 : tableData.length
    } else {
      return 1
    }
  }, [tableData])

  const handleTableSort = (_c, sort) => {
    if (tableData?.length) {
      const copyData = cloneDeep(tableData)
      copyData.sort((a, b) => {
        if (a[sort.key] === '-') {
          return 1
        }
        if (sort.type === 'desc') {
          return Number(a[sort.key]) > Number(b[sort.key]) ? -1 : 1
        } else {
          return Number(a[sort.key]) > Number(b[sort.key]) ? 1 : -1
        }
      })
      setTableData(copyData)
      setTableSort(sort)
    }
  }

  return (
    <div
      id={`dashboard-chart-${graph.graphId}`}
      className={className}
      style={{
        height: tableVisible ? `calc(358px + ${tableH * 40 + 80}px)` : '100%'
      }}
    >
      <div className="chart">
        <div className="header">
          <p className="title">
            {graph.title}
            {chartData?.lineCount > 1000 ? (
              <span className="tip">{_$('监控视图最多添加1000条数据')}</span>
            ) : null}
          </p>
          <div className="header-icon">
            <Icon
              size={20}
              className="ion-navicon-round"
              onClick={e => {
                setTableVisible(!tableVisible)
                e.stopPropagation()
              }}
            />
            <Icon
              size={20}
              className="ion-android-create"
              onClick={e => {
                history.push(
                  // eslint-disable-next-line max-len
                  `/monitor/dashboard/detail/editGraph/${dashboardID}/${dashboardName}/${graph.graphId}`
                )
              }}
            />
            <Icon
              size={20}
              className="ion-android-delete"
              onClick={e => {
                setDeleteVisible(true)
                e.stopPropagation()
              }}
            />
            <Icon
              size={20}
              className="ion-android-download"
              onClick={e => {
                exportChartData()
                // setExportVisible(true)
                e.stopPropagation()
              }}
            />
            <Icon
              size={20}
              className="ion-android-expand"
              onClick={e => {
                setDetailsVisible(true)
                e.stopPropagation()
              }}
            />
          </div>
        </div>
        <div className="content">
          <MonitorDashboardChart
            ref={chartItemRef}
            chartData={chartData}
            interval={chartData?.interval || 0}
          />
          {loading ? <Spin overlay /> : null}
        </div>
      </div>
      {tableVisible && (
        <div className="detail">
          <div className="detail-header">
            <Tooltip
              confirm
              theme="light"
              trigger="click"
              onOk={() => {
                setTableScheme(schemeChecked)
              }}
              onCancel={() => {
                setSchemeChecked(tableScheme)
              }}
              content={
                <div className="monitor-view-tooltip">
                  {chartTableScheme.map(({ key, title }) => (
                    <Checkbox
                      key={key}
                      name="chartTableScheme"
                      trueValue={key}
                      value={schemeChecked}
                      onChange={value => {
                        setSchemeChecked(value)
                      }}
                    >
                      {title}
                    </Checkbox>
                  ))}
                </div>
              }
            >
              <Icon size={20} className="icon ion-gear-b" />
            </Tooltip>
            {tableData?.length ? (
              <Icon size={20} className="icon ion-android-download" onClick={tableDataeExport} />
            ) : (
              <Icon
                size={20}
                className="icon ion-android-download table_export_disabled"
                // onClick={tableDataeExport}
              />
            )}
          </div>
          <div className="table">
            <Table
              scheme={scheme}
              data={tableData}
              checkType="none"
              sort={tableSort}
              noDataTemplate={allAggregator ? <p>该监控视图为实例聚合视图</p> : null}
              on$change-sort={handleTableSort}
            ></Table>
          </div>
        </div>
      )}
      <MonitorChartDelete
        visible={deleteVisible}
        closeDelete={() => {
          setDeleteVisible(false)
        }}
        dashboardId={dashboardID}
        productList={productList}
        graph={graph}
        reload={reload}
      />
      {/* <MonitorChartExport
        visible={exportVisible}
        closeExport={() => {
          setExportVisible(false)
        }}
        graph={graph}
        start={start}
        end={end}
        dashboardId={dashboardID}
      /> */}
      <MonitorChartDetails
        visible={detailsVisible}
        graph={graph}
        start={start}
        end={end}
        closeExport={() => {
          setDetailsVisible(false)
        }}
      />
    </div>
  )
}

export default styled(MonitorView)`
  width: 100%;
  padding-top: 10px;
  position: relative;
  .chart {
    width: 100%;
    padding: 10px;
    background: #fff;
    border: 1px solid #d7d7d7;
    position: relative;
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 8px;
      .title {
        font-size: 16px;
        margin: 0;
      }
      .tip {
        margin: 0 0 0 8px;
        font-size: 12px;
        color: ${variable.errorColor};
      }
      .header-icon {
        opacity: 0;
        transition: all ease-in-out 0.2s;
        .k-icon {
          margin-left: 8px;
          cursor: pointer;
          transition: all ease-in-out 0.2s;
          &:hover {
            color: ${variable.primaryColor};
          }
        }
      }
    }
    &:hover {
      .header-icon {
        opacity: 1;
      }
    }
    .content {
      height: 300px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
  .detail {
    position: absolute;
    bottom: 0;
    width: 100%;
    padding: 10px;
    padding-bottom: 11px;
    background: #fff;
    border: 1px solid #d7d7d7;
    border-top: none;
    transition: all ease-in-out 0.2s;
    max-height: 480px;
    overflow: auto;
    .detail-header {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      .icon {
        margin-left: 8px;
        cursor: pointer;
        transition: all ease-in-out 0.2s;
        &.table_export_disabled {
          color: #d0d5d9;
          cursor: no-drop;
          &:hover {
            color: #d0d5d9;
          }
        }
        &:hover {
          color: ${variable.primaryColor};
        }
      }
    }
    .table {
      margin-top: 4px;
      td {
        height: 40px;
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  }
`
